import { SxProps } from "@mui/material";
import { ITaskFilterSelectBox } from "../../components/Shared/Filter/types";
import { useStoreDispatch } from "../../store/hooks";
import {
  useGetAllTaskLogsQuery,
  useGetScheduledTasksQuery,
  useGetTaskLogsQuery,
  useGetTemplatesQuery,
  useDeleteTemplateMutation,
  useGetAllTemplateLogsQuery,
  useGetTaskLogDetailsQuery,
} from "../../store/api/tasks/taskApiSlice";
import { scheduledTasksColumns, templatesColumns, taskLogsColumns, allTaskLogsColumns, allTemplateLogsColumns, taskLogDetailsColumns } from "../../components/Shared/Table/Columns";
import { API } from "../../globals/globals";
import { setOpenToast } from "../../store/helpers/helperReducers";
import { useSelector } from "react-redux";
import { paginationModelSelector } from "../../store/helpers/helperSelectors";
import { useLocation, useNavigate } from "react-router-dom";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import ProfilePermissions from "../../utils/ProfilePermissions";
import { initialState } from "../../store/helpers/helperInitialState";
import { useEffect, useState, useMemo } from "react";
import { isTemplateSelector } from "../../store/api/tasks/selector";
import { selectedGeoRegionsSelector } from "../../store/api/admin/selectors";
import { IGeoRegion } from "../../store/api/admin/types";
import { useGetGoldenAuditDefinitionsQuery } from "../../store/api/audit/goldenAuditApiSlice";
import { searchKeywordSelector } from "../../store/search/selector";
import { goldenConfigurationColumns } from "../../components/Shared/Table/Columns";
import { taskFilterRegionIdsSelector, taskFilterTypeSelector, taskFilterStatusSelector } from "../../store/filters/tasks/selectors";
import { extractTemplateDeleteErrorMessage } from "./templateDeleteUtils";

export const useTasksFilter = () => {
  //const deviceFilterData = useSelector(devicesSelector);
  const dispatch = useStoreDispatch();
  //const versionList = useVersionsDisplay();
  //const deviceGroups = Object.values(DEVICEGROUPS);
  //const filterGroups = deviceGroups.filter((group) => group.Models.length).map((filter) => filter.Text);

  const filters: ITaskFilterSelectBox[] = [
    {
      id: "domain",
      label: "Domain",
      value: "",
      values: ["Connected", "Disconnected"],
      width: "140px",
    },
    {
      id: "nodetype",
      label: "Node Type",
      value: "",
      values: ["Synchronized", "Pending", "Commit Error", "Resynchronizing", "Synchronization Failed"],
      width: "130px",
    },
    {
      id: "nodetype",
      label: "Global And Local",
      value: "",
      values: ["Global", "Local"],
      width: "130px",
    },
  ];

  const formControlSx: SxProps = { minWidth: 100, marginRight: "10px" };

  return {
    dispatch,
    filters,
    formControlSx,
  };
};

export const useTaskDisplay = () => {

  const navigate = useNavigate();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const selectedGeoRegions = useSelector(selectedGeoRegionsSelector) as IGeoRegion[];
  
  // Get filter values from task filter state
  const regionFilterIds = useSelector(taskFilterRegionIdsSelector);
  const selectedTaskType = useSelector(taskFilterTypeSelector);
  const selectedTaskStatus = useSelector(taskFilterStatusSelector);

  // Determine if we're actively filtering by region
  const isFilteringByRegion = regionFilterIds && regionFilterIds.length > 0;

  // Use task filter region IDs if set, otherwise fall back to global selection
  const regionsToFilter = isFilteringByRegion
    ? selectedGeoRegions.filter((region) => regionFilterIds.includes(region.objectKey))
    : selectedGeoRegions;

  const regionNames = (regionsToFilter || [])
    .map((region) => region?.name)
    .filter((name): name is string => !!name && name.trim().length > 0);

  const geoRegionFilter = regionNames.length ? `geoRegion=${encodeURIComponent(regionNames.join(","))}` : "";
  
  // Add filter indicator to tell backend whether to include tasks with no region
  const filterByRegionParam = isFilteringByRegion ? "filterByRegion=true" : "filterByRegion=false";

  const filtersParts = [
    geoRegionFilter,
    filterByRegionParam,
  ].filter((part) => !!part);

  const args = {
    filters: filtersParts.join("&"),
  };

  const { data, isLoading, refetch } = useGetScheduledTasksQuery(args, {
    pollingInterval: API.ENDPOINTS.NODES.pollingInterval || API.POLLING_INTERVAL,
  });

  useEffect(() => {
    if (!!permissions) {
      if (!permissions?.hasOpCodeGroup("view_tasks")) {
        navigate("/nodes");
      }
    }
  }, [navigate, permissions]);

  // Apply client-side filtering for Type and Status
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let filtered = [...data];

    // Filter by task type
    if (selectedTaskType) {
      filtered = filtered.filter((task: any) => task.taskName === selectedTaskType);
    }

    // Filter by task status
    if (selectedTaskStatus) {
      filtered = filtered.filter((task: any) => task.status === selectedTaskStatus);
    }

    return filtered;
  }, [data, selectedTaskType, selectedTaskStatus]);

  // const newData = data != null && data?.length > 0 ? prepareDataForTasksTable(data) : [];
  // console.log('newData :>> ', newData);
  return {
    permissions,
    data: filteredData,
    isLoading,
    refetch,
    scheduledTasksColumns,
  };
};

export const useTaskLogsDisplay = (taskName: any, isOpen: boolean) => {
  const args = { taskName: taskName };
  const { data, isLoading } = useGetTaskLogsQuery(args, {
    pollingInterval: isOpen ? API.ENDPOINTS.TASKLOGS.pollingInterval : API.POLLING_INTERVAL,
  });

  const taskLogData = data;

  return {
    taskLogData,
    isLoading,
    taskLogsColumns,
  };
};

export const useTaskTemplateDisplay = () => {
  const dispatch = useStoreDispatch();
  const selectedTemplate = useSelector(isTemplateSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [sortOrder, setSortOrderToTaskTemplate] = useState("");
  const standardArgs = {
    filters: sortOrder ? `sort=${sortOrder}` : "",
  };

  const standardTemplatesResponse = useGetTemplatesQuery(standardArgs, {
    pollingInterval: API.ENDPOINTS.TEMPLATES.pollingInterval || API.POLLING_INTERVAL,
  });

  const [deleteTemplate] = useDeleteTemplateMutation();
  const data: any[] = standardTemplatesResponse.data || [];
  const isLoading = standardTemplatesResponse.isLoading;
  const refetch = standardTemplatesResponse.refetch;

  const handleDeleteTemplate = async (args: any, actionName: string) => {
    let response: any;
    const templateNameForToast = args?.template || args?.templateName || "template";
    try {
      if (actionName.match("delete")) {
        response = await deleteTemplate(args);
        if (response?.error) throw response?.error;
        if (response.data?.status !== 200) {
          throw response;
        }
        if (!response?.error) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `Template "${templateNameForToast}" deleted successfully`
            })
          );
        }
        refetch();
      }

    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: extractTemplateDeleteErrorMessage(error, templateNameForToast),
        })
      );
    }
  };

  return {
    data,
    isLoading,
    templatesColumns,
    handleDeleteTemplate,
    setSortOrderToTaskTemplate,
    permissions,
    selectedTemplate,
  };
};

export const useTaskLogsData = () => {
  const { taskLog = initialState.pagination } = useSelector(paginationModelSelector);
  const [sortOrder, setSortOrderToTaskLogs] = useState("");

  const args = {
    filters: `page=${taskLog.page.toString()}&size=${taskLog.pageSize.toString()}&sort=${sortOrder}`,
  };

  const { data, isLoading } = useGetAllTaskLogsQuery(args, {
    pollingInterval: API.ENDPOINTS.TEMPLATES.pollingInterval || API.POLLING_INTERVAL
  });

  return {
    data,
    isLoading,
    allTaskLogsColumns,
    setSortOrderToTaskLogs,
  };
};

export const useTemplateLogsData = () => {
  //const { templateLog = initialState.pagination } = useSelector(paginationModelSelector);
  const location = useLocation();
  const deviceKey = location?.pathname.split("/")[2] || "NoDevice";
  //const [sortOrder, setSortOrderToTemplateLogs] = useState("");

  const args = {
    filters: "",//`page=${templateLog.page.toString()}&size=${templateLog.pageSize.toString()}&sort=${sortOrder}`,
    deviceId: deviceKey
  };

  const { data, isLoading } = useGetAllTemplateLogsQuery(args, {
    pollingInterval: API.ENDPOINTS.TEMPLATELOGS.pollingInterval || API.POLLING_INTERVAL
  });

  return {
    data,
    isLoading,
    allTemplateLogsColumns,
    //setSortOrderToTemplateLogs
  };
};

export const useTaskLogsDetailsData = (taskName: string, taskExecutionKey: string) => {
  const args = {
    taskName: taskName || "",
    taskExecutionKey: taskExecutionKey || ""
  };

  const { data, isLoading } = useGetTaskLogDetailsQuery(args, {
    pollingInterval: API.ENDPOINTS.TEMPLATELOGS.pollingInterval || API.POLLING_INTERVAL
  });

  return {
    data,
    isLoading,
    taskLogDetailsColumns,
  };
};

export const useAuditDisplay = (nodeTypeFilter: string, technologyFilter: string, lastRunFilter: string) => {
  const searchKeyword = useSelector(searchKeywordSelector);

  const nodeTypeParam = nodeTypeFilter === "All" ? "" : nodeTypeFilter;
  const technologyParam = technologyFilter === "All" ? "" : technologyFilter;
  const lastRunStatusParam = lastRunFilter === "All" ? "" : lastRunFilter.toUpperCase();

  const filters = `search=${searchKeyword || ""}&nodeType=${nodeTypeParam}&radioTech=${technologyParam}&lastRunStatus=${lastRunStatusParam}`;

  const { data, isLoading, refetch } = useGetGoldenAuditDefinitionsQuery(
    { filters },
    { pollingInterval: API.ENDPOINTS.GOLDEN_AUDIT_DEFINITIONS.pollingInterval || API.POLLING_INTERVAL }
  );

  const rows = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.map((item) => ({
      id: item.id,
      definitionName: item.name,
      version: item.version,
      nodeType: item.nodeType,
      technology: item.radioTech,
      description: item.description ?? item.name,
      lastUpdated: item.updatedAt,
      lastRun: item.lastRun,
      scheduleEnabled: item.scheduleEnabled,
      passCount: item.passCount ?? 0,
      failCount: item.failCount ?? 0,
      skipCount: item.skipCount ?? 0,
      result: item.lastRunStatus ? item.lastRunStatus.charAt(0) + item.lastRunStatus.slice(1).toLowerCase() : undefined,
    }));
  }, [data]);

  return {
    rows,
    isLoading,
    refetch,
    goldenConfigurationColumns,
  };
};

